'use client';

import type { ReactNode } from 'react';
// eslint-disable-next-line react/no-use-context -- useContext is more stable for SSR/hydration than use()
import { createContext, useContext, useState } from 'react';
import { useStore } from 'zustand';

import type { CourseQuizGenerationLockStoreApi } from './store';
import { createCourseQuizGenerationLockStore } from './store';
import type { CourseQuizGenerationLockStore } from './types';

const CourseQuizGenerationLockStoreContext = createContext<CourseQuizGenerationLockStoreApi | undefined>(
  undefined,
);
const fallbackCourseQuizGenerationLockStore = createCourseQuizGenerationLockStore();

type CourseQuizGenerationLockStoreProviderProps = {
  children: ReactNode;
};

export function CourseQuizGenerationLockStoreProvider({
  children,
}: CourseQuizGenerationLockStoreProviderProps) {
  const [store] = useState<CourseQuizGenerationLockStoreApi>(() =>
    createCourseQuizGenerationLockStore(),
  );

  return (
    <CourseQuizGenerationLockStoreContext value={store}>
      {children}
    </CourseQuizGenerationLockStoreContext>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCourseQuizGenerationLockStore<T>(
  selector: (state: CourseQuizGenerationLockStore) => T,
): T {
  // eslint-disable-next-line react/no-use-context -- useContext is more stable for SSR/hydration
  const store = useContext(CourseQuizGenerationLockStoreContext);
  const resolvedStore = store ?? fallbackCourseQuizGenerationLockStore;
  return useStore(resolvedStore, selector);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCourseQuizGenerationLockStoreApi(): CourseQuizGenerationLockStoreApi {
  // eslint-disable-next-line react/no-use-context -- useContext is more stable for SSR/hydration
  const store = useContext(CourseQuizGenerationLockStoreContext);

  if (store === undefined) {
    throw new Error('useCourseQuizGenerationLockStoreApi must be used within CourseQuizGenerationLockStoreProvider');
  }

  return store;
}
